import { ChevronDownIcon } from "Icons/ChevronDown";
import { ChevronUpIcon } from "Icons/ChevronUp";
import { XCloseIcon } from "Icons/XClose";
import { IconType } from "Icons/types";
import { Tooltip as ToolTipAntd } from "antd";
import { uniq, uniqBy } from "lodash";

import React, { ReactNode, useState } from "react";

import { css } from "@emotion/react";

import { LocationSelectionInput } from "components/profile/LocationSelectionInput";

import { useAccount } from "contexts/AccountContext";

import { useCloseOnClickOutside } from "utils/libraryHooks";

import { uiGray, white } from "constants/colors";

import { Button, Props as ButtonProps } from "./Button";
import { ButtonWrapper } from "./ButtonWrapper";
import { Checkbox } from "./Checkbox";
import { OptionType } from "./OptionsMenu";
import { Radio } from "./Radio";
import { Typography } from "./Typography";

const style = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    width: "fit-content",
  }),
  innerOverlay: { boxShadow: "none", padding: 0, borderRadius: 12 },
  wrapperOptions: css({
    background: white,
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    boxShadow: "0px 0px 0px 1px #F5F6FA, 0px 0px 8px rgba(72, 40, 228, 0.15)",
  }),
  label: css({ marginBottom: 8, fontWeight: 400 }),
  ul: css({
    listStyleType: "none",
    margin: 0,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    borderBottom: `1px solid ${uiGray[10]}`,
  }),
  buttons: css({
    display: "flex",
    gap: 8,
    padding: 16,
  }),
  xBtn: css({
    display: "flex",
    justifyContent: "flex-end",
  }),
  header: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  search: css({
    height: 18,
  }),
};

type Props = {
  label: string;
  primaryButtonLabel?: string;
  onSubmit?: (value: string[] | string) => void;
  options?: OptionType[];
  allowMultiple?: boolean;
  title?: string;
  searchEnabled?: boolean;
  defaultValue?: string;
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
  IconLeft?: IconType;
  customCss?: { button: ButtonProps["customCss"] };
  secondaryComponent?: ReactNode;
};

export const DropdownFilter = ({
  options = [],
  label,
  title,
  primaryButtonLabel,
  allowMultiple = false,
  searchEnabled = false,
  onSubmit,
  defaultValue,
  setSelectedValues,
  selectedValues,
  IconLeft,
  customCss,
  secondaryComponent,
}: Props) => {
  const { accountProfile } = useAccount(false);
  const initialLocation =
    accountProfile && accountProfile.locationName
      ? { name: accountProfile.locationName }
      : undefined;
  const [updatedOptions, setUpdatedOptions] = useState<OptionType[]>(options);
  const [showOptions, setShowOptions] = useState(false);
  const listId = `dropdown-filter-${label.replaceAll(" ", "-")}`; // this is used to differentiate when multiple dropdown on same page
  const buttonId = `dropdown-filter-button-${label.replaceAll(" ", "-")}`;
  useCloseOnClickOutside(
    showOptions,
    setShowOptions,
    listId,
    { exceptSelectors: [`#${buttonId}`] },
    false
  );

  return (
    <ToolTipAntd
      placement="bottom"
      arrow={false}
      overlayInnerStyle={style.innerOverlay}
      overlay={
        <div css={style.wrapperOptions} id={listId}>
          <ul css={style.ul}>
            <div css={title ? style.header : undefined}>
              {title && (
                <Typography color={uiGray[70]} weight="medium" size="XXS">
                  {title}
                </Typography>
              )}
              <div css={style.xBtn}>
                <ButtonWrapper
                  ariaLabel="Close"
                  onClick={() => setShowOptions(false)}
                >
                  <XCloseIcon height={16} width={16} stroke="#3E3E3E" />
                </ButtonWrapper>
              </div>
            </div>
            {searchEnabled && ( // render the search input.
              <div data-testid="search-location">
                <LocationSelectionInput
                  hideFooter
                  showPlaceholder
                  location={initialLocation}
                  onSelect={(value) => {
                    const locationOption: OptionType = {
                      name: value.name,
                      label: value.name,
                    };
                    setUpdatedOptions((prevOptions) =>
                      uniqBy([...prevOptions, locationOption], "name").sort()
                    );
                    setSelectedValues(
                      uniq([...selectedValues, locationOption.name])
                    );
                  }}
                  displayLabel={false}
                />
              </div>
            )}
            {updatedOptions.map((option) => {
              return (
                <React.Fragment key={option.name}>
                  {allowMultiple ? ( // if allowMultiple is true then we render Checkboxes otherwise Radio buttons.
                    <Checkbox
                      label={option.label}
                      customCss={{ label: style.label }}
                      displayLabelRight
                      checked={selectedValues.includes(option.name)}
                      onChange={() => {
                        const isOptionSelected = selectedValues.includes(
                          option.name
                        );
                        if (isOptionSelected) {
                          // If the option is already selected, remove it from selectedValues
                          setSelectedValues(
                            selectedValues.filter(
                              (value) => value !== option.name
                            )
                          );
                        } else {
                          // If the option is not selected, add it to selectedValues
                          setSelectedValues([...selectedValues, option.name]);
                        }
                      }}
                    />
                  ) : (
                    <Radio
                      onChange={() => setSelectedValues([option.name])}
                      name={option.label}
                      isChecked={selectedValues[0] === option.name}
                      showLabel
                      customCss={style.label}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ul>
          {secondaryComponent}
          {onSubmit && (
            <div css={style.buttons}>
              <Button
                onClick={() => {
                  setShowOptions(false);
                }}
                background="outlined"
                color="black"
                size="small"
                isFullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onSubmit(allowMultiple ? selectedValues : selectedValues[0]);
                  setShowOptions(false);
                }}
                size="small"
                isFullWidth
              >
                {primaryButtonLabel}
              </Button>
            </div>
          )}
        </div>
      }
      open={showOptions}
    >
      <div css={style.wrapper}>
        <Button
          id={buttonId}
          onClick={() => {
            setShowOptions((state) => !state);
          }}
          IconRight={showOptions ? ChevronUpIcon : ChevronDownIcon}
          IconLeft={IconLeft}
          color={
            selectedValues.filter((val) => val !== defaultValue).length
              ? "black"
              : "gray"
          }
          size={"small"}
          customCss={customCss?.button}
        >
          {`${label}${
            allowMultiple && !!selectedValues.length
              ? ` (${selectedValues.length})`
              : ""
          }`}
        </Button>
      </div>
    </ToolTipAntd>
  );
};
